import { useState, useRef, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { assessmentRbacChecker } from "../../../../../utils/rbac";
import { optionTypeAsync } from "../../../../../app/patientSlice";
import { useReadOnly } from "../../../../../hooks/useReadOnly";

export const useSectionForm = ({
  interventionId,
  dataSelector,
  listSelector,
  fetchAction,
  patchAction,
  optionType,
  dataKey,
  hasDataFn,
  buildPayload,
  processSelection,
  callViewModeOnMount = false,
}) => {
  const isMultiSelect = typeof processSelection === "function";

  const [editable, setEditable] = useState(false);
  const [formChangesConfirm, setFormChangesConfirm] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [clickCancel, setClickCancel] = useState(false);

  // Only used by multi-select consumers
  const [listViewMenuData, setListViewMenuData] = useState([]);
  const listViewRef = useRef(null);

  const { register, handleSubmit, reset } = useForm();
  const dispatch = useDispatch();
  const { patientId, assessmentId } = useParams();

  const resp = useSelector(dataSelector);
  const listResp = useSelector(listSelector);
  const patientData = useSelector((state) => state.patient.detailViewData);
  const userDetails = useSelector((state) => state.user);

  const isReadOnly = useReadOnly();
  const hideButtons = assessmentRbacChecker(userDetails?.role, patientData?.isOwnCustomer);
  const sectionData = resp?.[dataKey];
  const hasData = hasDataFn(resp, dataKey);

  const viewMode = useCallback(() => {
    if (!hasData) return false;
    setEditable(false);
    reset();
  }, [hasData, reset]);

  const confirmationClose = useCallback(() => {
    setConfirmationModal(false);
    setClickCancel(false);
  }, []);

  const yesConfirmationModalClick = useCallback(() => {
    setConfirmationModal(false);
    setEditable(false);
    setFormChangesConfirm(false);
    setClickCancel(false);
    viewMode();
  }, [viewMode]);

  const editMode = useCallback(() => {
    setFormChangesConfirm(false);
    setClickCancel(false);
    setEditable(true);
  }, []);

  const handleCancelClick = useCallback(() => {
    if (!hasData) return false;
    setClickCancel(true);
  }, [hasData]);

  // Multi-select only — single-select consumers do not pass processSelection
  const handleListViewMenuChange = useCallback(
    (e) => {
      setFormChangesConfirm(true);
      processSelection(e, setListViewMenuData);
    },
    [processSelection]
  );

  // doSubmit receives RHF formData for single-select, ignored for multi-select
  const doSubmit = useCallback(
    (formData) => {
      const base = { patientId, assessmentId, interventionId };
      dispatch(patchAction(buildPayload(formData, base)));
      setEditable(false);
    },
    [dispatch, patchAction, buildPayload, patientId, assessmentId, interventionId]
  );

  useEffect(() => {
    if (!editable) return;
    if (formChangesConfirm && clickCancel) {
      setConfirmationModal(true);
    } else if (clickCancel && !formChangesConfirm) {
      setClickCancel(false);
      setConfirmationModal(false);
      setEditable(false);
    }
  }, [formChangesConfirm, clickCancel, editable]);

  // multi-select only
  useEffect(() => {
    if (!isMultiSelect) return;
    if (!(dataKey in (resp ?? {}))) return;
    if (!hasData) setEditable(true);
    const data = Array.isArray(sectionData) ? sectionData.filter((d) => d && d.key !== "") : [];
    setListViewMenuData(data);
  }, [resp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open edit when empty (single-select)
  useEffect(() => {
    if (isMultiSelect) return;
    if (!(dataKey in (resp ?? {}))) return;
    if (!hasData) setEditable(true);
    else setEditable(false);
  }, [resp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (callViewModeOnMount) viewMode();
    dispatch(optionTypeAsync(optionType));
    dispatch(fetchAction({ patientId, assessmentId, interventionId }));
  }, [interventionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    editable,
    confirmationModal,
    listViewMenuData, // multi-select consumers only
    listViewRef, // multi-select consumers only
    resp,
    sectionData,
    listResp,
    hideButtons,
    isReadOnly,
    handleSubmit,
    register,
    setFormChangesConfirm,
    confirmationClose,
    yesConfirmationModalClick,
    editMode,
    handleListViewMenuChange, // multi-select consumers only
    handleCancelClick,
    doSubmit,
  };
};
